import { envManager } from "../config/env";

/**
 * UTM URL 生成工具类
 * 为AI提供的产品链接动态添加UTM标签(支持多市场)
 */
export class UtmUrlHelper {
  /**
   * 生成带有 UTM 参数的 URL
   * @param originUrl
   * @param skuName
   * @returns {string} 带有 UTM 参数的完整 URL
   */
  static generateUtmUrl(originUrl: string, skuName: string): string {
    if (!originUrl) {
      return originUrl;
    }

    let urlWithUtm = originUrl;

    urlWithUtm = this.addMarketUtmInfo(originUrl, skuName);

    return urlWithUtm;
  }

  static addMarketUtmInfo(originUrl: string, skuName: string): string {
    if (!originUrl) {
      return originUrl;
    }

    // 获取当前市场，默认回退到 sg
    const currentMarket = envManager.getCurrentMarket()?.toLowerCase() || "sg";

    // 按照新需求处理 SKU 名称：
    // 1. 去除产品名称中的英文逗号
    // 2. 去除产品名称中的&及其左边相邻的空格
    // 3. 使用英文横线-替换产品名称中的空格
    let cleanedSkuName = skuName
      .replace(/,/g, "") // 去除英文逗号
      .replace(/ &/g, "") // 去除&及其左边相邻的空格
      .replace(/ /g, "-"); // 用横线替换空格

    // 构建UTM参数
    const utmCampaign = `${currentMarket}-genai-shop-assist-${cleanedSkuName}`;
    const utmParams = `utm_campaign=${utmCampaign}&utm_source=${currentMarket}-genai-shop-assist&utm_medium=online`;

    return this.appendUtmParams(originUrl, utmParams);
  }

  /**
   * 将 UTM 参数追加到 URL
   * @param {string} originUrl - 原始 URL
   * @param {string} utmParams - UTM 参数字符串
   * @returns {string} 带有 UTM 参数的完整 URL
   */
  static appendUtmParams(originUrl: string, utmParams: string): string {
    if (!originUrl) {
      return originUrl;
    }

    // 如果 URL 中没有查询参数
    if (!originUrl.includes("?")) {
      return `${originUrl}?${utmParams}`;
    }

    // 如果 URL 中有查询参数
    if (originUrl.includes("?")) {
      // 如果 URL 以 ? 结尾
      if (originUrl.endsWith("?")) {
        return `${originUrl}${utmParams}`;
      }
      // 如果 URL 中有其他查询参数
      return `${originUrl}&${utmParams}`;
    }

    return originUrl;
  }

  /**
   * 解析 URL 中的 UTM 参数
   * @param {string} url - 包含 UTM 参数的 URL
   * @returns {Object} UTM 参数对象
   */
  static parseUtmParams(url: string): Record<string, string> {
    if (!url || !url.includes("?")) {
      return {};
    }

    const urlObj = new URL(url);
    const utmParams: Record<string, string> = {};

    const utmKeys = [
      "utm_campaign",
      "utm_source",
      "utm_medium",
      "utm_term",
      "utm_content",
    ];

    utmKeys.forEach((key) => {
      const value = urlObj.searchParams.get(key);
      if (value) {
        utmParams[key] = value;
      }
    });

    return utmParams;
  }

  /**
   * 验证 UTM 参数是否完整
   * @param {string} url - 要验证的 URL
   * @returns {boolean} 是否包含完整的 UTM 参数
   */
  static validateUtmParams(url: string): boolean {
    const utmParams = this.parseUtmParams(url);
    const requiredParams = ["utm_campaign", "utm_source", "utm_medium"];

    return requiredParams.every((param) => utmParams.hasOwnProperty(param));
  }
}
